import { AxiosInstance } from 'axios';
import { getCurrentBearerToken } from '../observables/bearerTokenObservable';

/**
 *
 * @param axiosInstance An {@link AxiosInstance} to decorate with authentication interceptors. On each request, bearer token is added in Authentication header. If response fails with 401, the response interceptor tries to obtain a new token and retry the request (once).
 * @returns A void cleanup function to remove authentication interceptors from the axios instance.
 */
export const addAuthenticationInterceptors = (axiosInstance: AxiosInstance) => {
  const requestInterceptor = axiosInstance.interceptors.request.use(
    (config) => {
      const token = getCurrentBearerToken()?.idToken ?? window.bearerToken;
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }
  );

  const responseInterceptor = axiosInstance.interceptors.response.use(
    null,
    async (error: any) => {
      const { config, response } = error;
      if (response?.status === 401 && !config?._retry) {
        const refresh = getCurrentBearerToken()?.refresh;
        if (refresh) {
          await refresh();
          config._retry = true;
          return await axiosInstance(config);
        }
      }
      return Promise.reject(error);
    }
  );

  const removeInterceptors = () => {
    axiosInstance.interceptors.request.eject(requestInterceptor);
    axiosInstance.interceptors.response.eject(responseInterceptor);
  };

  return removeInterceptors;
};
